/**
 * ReportRow — single list item in MineList.
 *
 * Shows status / type / severity badges, a one-line description preview,
 * a relative timestamp, and a "N replies" hint pulled from the
 * comment_count annotation on the backend. Whole row is the click
 * target so keyboard nav (Tab + Enter) works.
 */

import { h } from 'preact'

import type { WidgetReportRow } from '../types'
import type { StringKey } from './i18n'

interface ReportRowProps {
  row: WidgetReportRow
  strings: Record<StringKey, string>
  onClick: () => void
}

function statusClassName(status: string): string {
  return `pill pill-status pill-status--${status}`
}

function severityClassName(severity: string): string {
  return `pill pill-severity pill-severity--${severity}`
}

function typeClassName(): string {
  return 'pill pill-type'
}

function formatRelative(iso: string): string {
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return ''
  const seconds = Math.max(1, Math.round((Date.now() - then) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h`
  const days = Math.round(hours / 24)
  return `${days}d`
}

function repliesLabel(count: number, strings: Record<StringKey, string>): string {
  if (count === 1) return strings['mine.replies_one']
  return strings['mine.replies_many'].replace('{count}', String(count))
}

export function ReportRow({ row, strings, onClick }: ReportRowProps) {
  const preview =
    row.description.length > 120
      ? row.description.slice(0, 117) + '…'
      : row.description
  return (
    <button type="button" class="mine-row" onClick={onClick}>
      <div class="mine-row-pills">
        <span class={statusClassName(row.status)}>{strings[`status.${row.status}` as StringKey] ?? row.status}</span>
        <span class={typeClassName()}>{strings[`type.${row.feedback_type}` as StringKey]}</span>
        <span class={severityClassName(row.severity)}>{strings[`severity.${row.severity}` as StringKey]}</span>
      </div>
      <div class="mine-row-preview">{preview}</div>
      <div class="mine-row-meta">
        <span>{formatRelative(row.updated_at || row.created_at)}</span>
        {row.comment_count > 0 && <span>· {repliesLabel(row.comment_count, strings)}</span>}
      </div>
    </button>
  )
}
